"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useContactFormMutation } from "@marketing/home/lib/api";
import {
	type ContactFormValues,
	contactFormSchema,
	contactInterestOptions,
} from "@repo/api/src/routes/contact/types";
import { Alert, AlertTitle } from "@ui/components/alert";
import { Button } from "@ui/components/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@ui/components/form";
import { Input } from "@ui/components/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@ui/components/select";
import { Textarea } from "@ui/components/textarea";
import { MailCheckIcon, MailIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useAnalytics } from "@analytics";
import { MARKETING_EVENTS } from "@repo/analytics";
import type { ContactFormSubmittedData } from "@repo/analytics";

export function ContactForm() {
	const t = useTranslations();
	const contactFormMutation = useContactFormMutation();
	const { trackEvent } = useAnalytics();

	const form = useForm<ContactFormValues>({
		resolver: zodResolver(contactFormSchema),
		defaultValues: {
			name: "",
			email: "",
			message: "",
			interest: "general",
		},
	});

	// Preselect the interest from ?interest=… (services page CTAs link here
	// with their offering preselected). Read via window.location instead of
	// useSearchParams so the form needs no Suspense boundary. The version
	// bump remounts the (uncontrolled) Select so Radix picks the new value
	// up as its initial value; setting a controlled Radix Select's value
	// after mount, while its items are unregistered, makes it reset to "".
	const [prefillVersion, setPrefillVersion] = useState(0);
	useEffect(() => {
		const param = new URLSearchParams(window.location.search).get(
			"interest",
		);
		if (
			param &&
			(contactInterestOptions as readonly string[]).includes(param)
		) {
			form.setValue(
				"interest",
				param as ContactFormValues["interest"],
			);
			setPrefillVersion((version) => version + 1);
		}
	}, [form]);

	// watch() subscribes this component so the prefill re-renders the field.
	const interestValue = form.watch("interest") ?? "general";

	const onSubmit = form.handleSubmit(async (values) => {
		try {
			await contactFormMutation.mutateAsync(values);
			
			// Track successful contact form submission
			trackEvent(MARKETING_EVENTS.CONTACT_FORM_SUBMITTED, {
				name: values.name,
				email: values.email,
				messageLength: values.message.length,
			} as ContactFormSubmittedData);
		} catch {
			form.setError("root", {
				message: t("contact.form.notifications.error"),
			});
		}
	});

	return (
		<div>
			{form.formState.isSubmitSuccessful ? (
				<Alert variant="success">
					<MailCheckIcon className="size-6" />
					<AlertTitle>
						{t("contact.form.notifications.success")}
					</AlertTitle>
				</Alert>
			) : (
				<Form {...form}>
					<form
						onSubmit={onSubmit}
						className="flex flex-col items-stretch gap-4"
					>
						{form.formState.errors.root?.message && (
							<Alert variant="error">
								<MailIcon className="size-6" />
								<AlertTitle>
									{form.formState.errors.root.message}
								</AlertTitle>
							</Alert>
						)}

						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t("contact.form.name")}
									</FormLabel>
									<FormControl>
										<Input {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="email"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t("contact.form.email")}
									</FormLabel>
									<FormControl>
										<Input {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="interest"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t("contact.form.interest.label")}
									</FormLabel>
									<Select
										key={prefillVersion}
										onValueChange={field.onChange}
										defaultValue={interestValue}
									>
										<FormControl>
											<SelectTrigger>
												{/* Explicit children: labels for
												    values set after mount (URL
												    prefill) resolve without
												    opening the menu. */}
												<SelectValue
													placeholder={t(
														"contact.form.interest.placeholder",
													)}
												>
													{t(
														`contact.form.interest.options.${interestValue}` as any,
													)}
												</SelectValue>
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{contactInterestOptions.map(
												(option) => (
													<SelectItem
														key={option}
														value={option}
													>
														{t(
															`contact.form.interest.options.${option}` as any,
														)}
													</SelectItem>
												),
											)}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="message"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t("contact.form.message")}
									</FormLabel>
									<FormControl>
										<Textarea {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<Button
							type="submit"
							className="w-full"
							loading={form.formState.isSubmitting}
						>
							{t("contact.form.submit")}
						</Button>
					</form>
				</Form>
			)}
		</div>
	);
}
